'use client';

/**
 * BreadcrumbNav — breadcrumb interaktif (port MGMX): 3 segment (Group / SubGroup / Item) dari `menuGroups`,
 * tiap segment punya dropdown sibling untuk pindah menu tanpa membuka sidebar.
 *
 * Responsive — SELALU satu baris: ≥1280px semua level, 768–1279 dua level terakhir, <768 hanya level terakhir.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { buildBreadcrumbForMenu } from '@/lib/menuTree';

interface BreadcrumbNavProps {
    menuName: string;
    className?: string;
}

const cx = (...a: Array<string | false | null | undefined>) => a.filter(Boolean).join(' ');

function BreadcrumbDropdown({
    label, options, isActive,
}: {
    label: string;
    options: { label: string; href: string }[];
    isActive: boolean;
}) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={wrapRef} className="relative inline-flex">
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className={cx(
                    'inline-flex items-center gap-0.5 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer pointer-coarse:py-2',
                    isActive ? 'text-slate-700 font-medium max-lg:font-bold max-lg:text-slate-900 hover:text-sky-700' : 'hover:text-slate-700',
                )}
            >
                <span className="truncate max-w-[42vw] md:max-w-[20rem]">{label}</span>
                <ChevronDown size={10} className={cx('shrink-0 transition-transform max-lg:w-4 max-lg:h-4', open && 'rotate-180')} />
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[85vw] max-h-[min(70vh,28rem)] overflow-y-auto overscroll-contain bg-white/95 backdrop-blur-xl border border-sky-100 rounded-xl shadow-[0_8px_24px_rgba(14,116,144,0.18)] py-1 text-sm">
                    {options.map((opt, i) => {
                        const isCurrent = opt.label === label;
                        return (
                            <Link
                                key={i}
                                href={opt.href}
                                onClick={() => setOpen(false)}
                                className={cx(
                                    'block px-3 py-1.5 pointer-coarse:py-3 transition-colors',
                                    isCurrent
                                        ? 'bg-sky-100/70 text-sky-800 font-semibold cursor-default'
                                        : 'text-slate-700 hover:bg-sky-50 hover:text-sky-700',
                                )}
                            >
                                {opt.label}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/** Kelas visibilitas per level: terakhir selalu tampil, sebelumnya 2 level terakhir ≥768, sisanya ≥1280. */
function visibilityClass(idx: number, total: number): string {
    if (idx === total - 1) return 'flex';
    if (idx === total - 2) return 'hidden md:flex';
    return 'hidden xl:flex';
}

export default function BreadcrumbNav({ menuName, className }: BreadcrumbNavProps) {
    const items = useMemo(() => buildBreadcrumbForMenu(menuName), [menuName]);
    if (!items || items.length === 0) return null;

    return (
        // flex-nowrap + whitespace-nowrap: tidak pernah wrap ke baris baru (menjaga tinggi header tetap).
        // TIDAK pakai overflow-hidden supaya dropdown (absolute) tidak terpotong.
        <nav className={cx('flex flex-nowrap items-center gap-x-1 text-xs max-lg:text-[17px] text-slate-500 whitespace-nowrap min-w-0', className)} aria-label="breadcrumb">
            {items.map((item, idx) => {
                const isLast = idx === items.length - 1;
                const vis = visibilityClass(idx, items.length);
                return (
                    <span key={idx} className="contents">
                        {idx > 0 && (
                            // Pemisah ikut visibilitas item SEBELUMNYA
                            <span className={cx('items-center', visibilityClass(idx - 1, items.length))}>
                                <ChevronRight size={10} className="text-slate-300" />
                            </span>
                        )}
                        <span className={cx('items-center gap-1 min-w-0', vis)}>
                            {item.options && item.options.length > 0 ? (
                                <BreadcrumbDropdown label={item.label} options={item.options} isActive={isLast} />
                            ) : item.href && !isLast ? (
                                <Link href={item.href} className="hover:text-slate-700 hover:underline">{item.label}</Link>
                            ) : (
                                <span className={cx('truncate max-w-[42vw] md:max-w-[20rem]', isLast && 'text-slate-700 font-medium max-lg:font-bold max-lg:text-slate-900')}>
                                    {item.label}
                                </span>
                            )}
                        </span>
                    </span>
                );
            })}
        </nav>
    );
}
